import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useCallback, useEffect, useState } from 'react'
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
} from 'lexical'
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
} from '@lexical/list'
import { $setBlocksType } from '@lexical/selection'
import { $createHeadingNode } from '@lexical/rich-text'
import { $createParagraphNode } from 'lexical'
import { TOGGLE_LINK_COMMAND } from '@lexical/link'

export interface ToolbarPluginProps {
  accentColor?: 'blue' | 'orange' | 'green' | 'purple'
}

export default function ToolbarPlugin({ accentColor = 'blue' }: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext()
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)

  const accentColors = {
    blue: {
      button: 'text-blue-600 dark:text-blue-400',
      active: 'bg-blue-200 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
    },
    orange: {
      button: 'text-orange-600 dark:text-orange-400',
      active: 'bg-orange-200 dark:bg-orange-900 text-orange-900 dark:text-orange-100'
    },
    green: {
      button: 'text-green-600 dark:text-green-400',
      active: 'bg-green-200 dark:bg-green-900 text-green-900 dark:text-green-100'
    },
    purple: {
      button: 'text-purple-600 dark:text-purple-400',
      active: 'bg-purple-200 dark:bg-purple-900 text-purple-900 dark:text-purple-100'
    }
  }

  const colors = accentColors[accentColor]

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
    }
  }, [])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar()
      })
    })
  }, [editor, updateToolbar])

  const formatHeading = (headingSize: 'h1' | 'h2' | 'h3') => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(headingSize))
      }
    })
  }

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode())
      }
    })
  }

  const insertLink = () => {
    const url = prompt('URL eingeben:')
    if (url) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700">
      {/* Undo/Redo */}
      <button
        type="button"
        onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-300"
        title="Rückgängig"
      >
        ↶
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-300"
        title="Wiederholen"
      >
        ↷
      </button>

      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>

      {/* Text Format */}
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        className={`px-3 py-1 font-bold rounded transition-colors ${
          isBold ? colors.active : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
        }`}
        title="Fett"
      >
        B
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        className={`px-3 py-1 italic rounded transition-colors ${
          isItalic ? colors.active : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
        }`}
        title="Kursiv"
      >
        I
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        className={`px-3 py-1 underline rounded transition-colors ${
          isUnderline ? colors.active : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
        }`}
        title="Unterstrichen"
      >
        U
      </button>

      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>

      {/* Headings */}
      <button
        type="button"
        onClick={() => formatHeading('h1')}
        className="px-3 py-1 font-bold text-lg hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-300"
        title="Überschrift 1"
      >
        H1
      </button>
      <button
        type="button"
        onClick={() => formatHeading('h2')}
        className="px-3 py-1 font-bold hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-300"
        title="Überschrift 2"
      >
        H2
      </button>
      <button
        type="button"
        onClick={() => formatHeading('h3')}
        className="px-3 py-1 font-bold hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-300"
        title="Überschrift 3"
      >
        H3
      </button>
      <button
        type="button"
        onClick={formatParagraph}
        className="px-3 py-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-300"
        title="Normal"
      >
        P
      </button>

      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>

      {/* Lists */}
      <button
        type="button"
        onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
        className="px-3 py-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-300"
        title="Aufzählungsliste"
      >
        • Liste
      </button>
      <button
        type="button"
        onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
        className="px-3 py-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-300"
        title="Nummerierte Liste"
      >
        1. Liste
      </button>

      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>

      {/* Link */}
      <button
        type="button"
        onClick={insertLink}
        className={`px-3 py-1 ${colors.button} hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors`}
        title="Link einfügen"
      >
        🔗 Link
      </button>
    </div>
  )
}
